import dJSON from 'dirty-json';
import Splide from '@splidejs/splide';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';

const moduleSelector = '.vertical-carousels';
const CAROUSEL_KEYS = ['carousel_1', 'carousel_2', 'carousel_3'];
const MIN_LOOP_SLIDES = 8;
const HORIZONTAL_MQ = window.matchMedia('(max-width: 991px)');

/**
 * Module Init
 */
(() => {
  const modules = document.querySelectorAll(moduleSelector);
  if (!modules.length) return;

  modules.forEach((query) => {
    try {
      query.querySelector('script')?.remove();

      const { data } = dJSON.parse(query.data);
      const wrapper = query.querySelector('.carousels-wrapper');
      if (!wrapper || !data) return;

      const instances = [];
      const galleries = [];

      CAROUSEL_KEYS.forEach((key, index) => {
        const items = Array.isArray(data[key]) ? data[key].filter((item) => item?.image) : [];
        if (!items.length) return;

        wrapper.insertAdjacentHTML('beforeend', /* html */ `
          <section class="splide" data-carousel="${index + 1}">
            <div class="splide__track">
              <div class="splide__list"></div>
            </div>
          </section>
        `);

        const splideEl = wrapper.lastElementChild;
        const list = splideEl.querySelector('.splide__list');
        const slides = padSlides(items);

        slides.forEach((item, slideIndex) => {
          const sourceIndex = slideIndex % items.length;
          list.insertAdjacentHTML('beforeend', /* html */ `
            <div class="splide__slide">
              <div class="vertical-carousels__item">
                <button
                  type="button"
                  class="vertical-carousels__zoom"
                  data-gallery="${index}"
                  data-index="${sourceIndex}"
                  aria-label="${escapeAttr(zoomLabel(item.alt))}"
                >
                  <img src="${escapeAttr(item.image)}" alt="${escapeAttr(item.alt)}" />
                </button>
              </div>
            </div>
          `);
        });

        galleries[index] = items;
        instances.push({
          el: splideEl,
          index,
          slider: mountCarousel(splideEl, index),
        });
      });

      const lightbox = mountLightbox(query, galleries, () => instances);

      const onBreakpoint = () => {
        instances.forEach((instance) => {
          instance.slider.destroy();
          instance.slider = mountCarousel(instance.el, instance.index);
        });
        lightbox.syncMotion();
      };

      if (HORIZONTAL_MQ.addEventListener) {
        HORIZONTAL_MQ.addEventListener('change', onBreakpoint);
      } else {
        HORIZONTAL_MQ.addListener(onBreakpoint);
      }
    } catch (error) {
      console.error(`Error: ${moduleSelector}`, error);
    }
  });
})();

function getOptions(isHorizontal, index) {
  const autoScroll = {
    speed: index === 1 ? -0.5 : 0.5,
    pauseOnHover: false,
    pauseOnFocus: false,
  };

  if (isHorizontal) {
    return {
      direction: 'ltr',
      type: 'loop',
      autoWidth: true,
      height: '280px',
      gap: '16px',
      pagination: false,
      arrows: false,
      drag: false,
      clones: 4,
      autoplay: false,
      autoScroll,
    };
  }

  return {
    direction: 'ttb',
    type: 'loop',
    autoHeight: true,
    height: '1000px',
    gap: '30px',
    pagination: false,
    arrows: false,
    drag: false,
    clones: 4,
    autoplay: false,
    autoScroll,
  };
}

function mountCarousel(splideEl, index) {
  const isHorizontal = HORIZONTAL_MQ.matches;
  splideEl.classList.toggle('is-horizontal', isHorizontal);

  const slider = new Splide(splideEl, getOptions(isHorizontal, index));
  slider.mount({ AutoScroll });
  refreshWhenImagesLoad(slider, splideEl);
  return slider;
}

function refreshWhenImagesLoad(slider, splideEl) {
  splideEl.querySelectorAll('img').forEach((img) => {
    if (img.complete) return;
    img.addEventListener('load', () => slider.refresh(), { once: true });
  });
}

function padSlides(items) {
  if (items.length >= MIN_LOOP_SLIDES) return items;

  const padded = [];
  while (padded.length < MIN_LOOP_SLIDES) {
    padded.push(...items);
  }
  return padded;
}

function mountLightbox(root, galleries, getInstances) {
  root.insertAdjacentHTML('beforeend', /* html */ `
    <div class="vertical-carousels__lightbox" role="dialog" aria-modal="true" aria-label="Image preview" hidden>
      <button type="button" class="vertical-carousels__lightbox-close" aria-label="Close">
        <span aria-hidden="true">&times;</span>
      </button>
      <button type="button" class="vertical-carousels__lightbox-prev" aria-label="Previous image">
        <span aria-hidden="true">&#8249;</span>
      </button>
      <img class="vertical-carousels__lightbox-image" alt="" />
      <button type="button" class="vertical-carousels__lightbox-next" aria-label="Next image">
        <span aria-hidden="true">&#8250;</span>
      </button>
    </div>
  `);

  const dialog = root.querySelector('.vertical-carousels__lightbox');
  const image = dialog.querySelector('.vertical-carousels__lightbox-image');
  const closeBtn = dialog.querySelector('.vertical-carousels__lightbox-close');
  const prevBtn = dialog.querySelector('.vertical-carousels__lightbox-prev');
  const nextBtn = dialog.querySelector('.vertical-carousels__lightbox-next');
  let current = null;
  let lastFocus = null;
  let previousOverflow = '';

  const itemsFor = () => (current ? galleries[current.gallery] || [] : []);

  const render = () => {
    const items = itemsFor();
    const item = items[current.index];
    if (!item) return;
    image.src = item.image;
    image.alt = item.alt || '';
    const multi = items.length > 1;
    prevBtn.hidden = !multi;
    nextBtn.hidden = !multi;
  };

  const setMotion = (playing) => {
    getInstances().forEach(({ slider }) => {
      const autoScroll = slider.Components?.AutoScroll;
      if (!autoScroll) return;
      if (playing) autoScroll.play();
      else autoScroll.pause();
    });
  };

  const onKeydown = (event) => {
    if (!current) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      step(1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      step(-1);
    }
  };

  const open = (gallery, index, trigger) => {
    const items = galleries[gallery];
    if (!items?.length) return;
    current = { gallery, index };
    lastFocus = trigger;
    render();
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.hidden = false;
    setMotion(false);
    document.addEventListener('keydown', onKeydown);
    closeBtn.focus();
  };

  const close = () => {
    if (!current) return;
    current = null;
    dialog.hidden = true;
    image.removeAttribute('src');
    image.alt = '';
    document.body.style.overflow = previousOverflow;
    document.removeEventListener('keydown', onKeydown);
    setMotion(true);
    lastFocus?.focus();
    lastFocus = null;
  };

  const step = (delta) => {
    const items = itemsFor();
    if (items.length < 2 || !current) return;
    current.index = (current.index + delta + items.length) % items.length;
    render();
  };

  root.querySelector('.carousels-wrapper')?.addEventListener('click', (event) => {
    const trigger = event.target.closest('.vertical-carousels__zoom');
    if (!trigger) return;
    open(Number(trigger.dataset.gallery), Number(trigger.dataset.index), trigger);
  });

  closeBtn.addEventListener('click', close);
  prevBtn.addEventListener('click', () => step(-1));
  nextBtn.addEventListener('click', () => step(1));
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close();
  });

  return {
    syncMotion() {
      if (current) setMotion(false);
    },
  };
}

function zoomLabel(alt) {
  const label = String(alt || '').trim();
  return label ? `View larger image: ${label}` : 'View larger image';
}

function escapeAttr(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}
